import api from './client';
import axios from 'axios';
import { useApiConfigStore } from '@store/api_config';
import type {
  EstimateExtraction,
  GeneratePayload,
  GenerateResult,
  ClientDevisData,
} from 'types/estimateExtraction';

const publicClient = axios.create();
publicClient.interceptors.request.use((config) => {
  config.baseURL = useApiConfigStore.getState().getApiUrl();
  return config;
});

export const estimateExtractionApi = {
  importServicebox: async (ordreId: string, html: string): Promise<EstimateExtraction> => {
    const { data } = await api.post('/estimate-extractions/import-servicebox', { ordre_id: ordreId, html });
    return data;
  },

  extract: async (ordreId: string, file: File, group: string = "initial"): Promise<EstimateExtraction> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('ordre_id', ordreId);
    formData.append('group', group);
    const { data } = await api.post('/estimate-extractions/extract', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return data;
  },

  get: async (id: string): Promise<EstimateExtraction> => {
    const { data } = await api.get(`/estimate-extractions/${id}`);
    return data;
  },

  generate: async (id: string, payload: GeneratePayload): Promise<GenerateResult> => {
    const { data } = await api.post(`/estimate-extractions/${id}/generate`, payload);
    return data;
  },

  listByOrdre: async (ordreId: string): Promise<EstimateExtraction[]> => {
    const { data } = await api.get(`/estimate-extractions/ordre/${ordreId}`);
    return data;
  },

  createToken: async (
    extractionId: string,
    payload: {
      selected_item_ids: string[];
      obligatoire_item_ids: string[];
      tva_rate: number;
      reminders_enabled?: boolean;
      disabled_reminders?: string[];
    },
  ): Promise<{ token: string; url: string }> => {
    const { data } = await api.post(`/estimate-extractions/${extractionId}/create-token`, payload);
    return data;
  },

  // Reminder management
  getTokenReminders: async (tokenId: string): Promise<{ reminders: any[]; reminders_enabled: boolean }> => {
    const { data } = await api.get(`/estimate-extractions/${tokenId}/reminders`);
    return data;
  },

  updateReminder: async (
    tokenId: string,
    reminderId: string,
    data: { scheduled_at?: string; status?: string },
  ): Promise<{ status: string; reminders: any[] }> => {
    const { data: res } = await api.patch(
      `/estimate-extractions/${tokenId}/reminders/${reminderId}`,
      data,
    );
    return res;
  },

  cancelAllReminders: async (tokenId: string): Promise<{ status: string; reminders: any[] }> => {
    const { data } = await api.post(`/estimate-extractions/${tokenId}/reminders/cancel-all`);
    return data;
  },

  sendReminderNow: async (tokenId: string): Promise<any> => {
    const { data } = await api.post(`/estimate-extractions/${tokenId}/reminders/send-now`);
    return data;
  },

  // Public (no auth)
  getClientDevis: async (tokenId: string): Promise<ClientDevisData> => {
    const { data } = await publicClient.get(`/estimate-extractions/devis/${tokenId}`);
    return data;
  },

  submitClientSelection: async (tokenId: string, selectedItemIds: string[]): Promise<{ status: string; pdf_url: string }> => {
    const { data } = await publicClient.post(`/estimate-extractions/devis/${tokenId}`, {
      selected_item_ids: selectedItemIds,
    });
    return data;
  },

  signDevis: async (tokenId: string, signatureFile: File, otpSessionToken?: string): Promise<{ status: string; pdf_url?: string; ordre_id?: string }> => {
    const formData = new FormData();
    formData.append('file', signatureFile);
    const headers: Record<string, string> = { 'Content-Type': 'multipart/form-data' };
    if (otpSessionToken) {
      headers['X-OTP-Session-Token'] = otpSessionToken;
    }
    const { data } = await publicClient.post(`/estimate-extractions/devis/${tokenId}/sign`, formData, { headers });
    return data;
  },

  sendDevisOtp: async (tokenId: string): Promise<{ success: boolean; message: string }> => {
    const { data } = await publicClient.post(`/estimate-extractions/devis/${tokenId}/send-otp`);
    return data;
  },

  verifyDevisOtp: async (tokenId: string, code: string): Promise<{ success: boolean; message: string; session_token?: string }> => {
    const { data } = await publicClient.post(`/estimate-extractions/devis/${tokenId}/verify-otp`, { code });
    return data;
  },
};
